import { Modal, Badge, Toast } from "react-bootstrap";
import { useState, useEffect, useRef } from "react";
import Api from "../../Auth/Api";
import { decodeJWT } from "../../Components/jwtUtils";

function ModalDetalhesAgendamento({ show, onHide, agendamento, onRelatorioCriticoChange }) {
    const [isUploading, setIsUploading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [showModalConfirmacaoRemover, setShowModalConfirmacaoRemover] = useState(false);
    const [agendamentoAtualizado, setAgendamentoAtualizado] = useState(agendamento);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [showScrollBottom, setShowScrollBottom] = useState(false);
    const [isDownloadingListaPresenca, setIsDownloadingListaPresenca] = useState(false);
    const modalBodyRef = useRef(null);
    const token = JSON.parse(localStorage.getItem("user_token"));
    const nivel = decodeJWT(token).nivel;
    const isAdmin = nivel === 'Administrador';
    const podeBaixarListaPresenca = ['Administrador', 'Gestor'].includes(nivel);

    // Atualizar agendamento quando prop mudar
    useEffect(() => {
        setAgendamentoAtualizado(agendamento);
    }, [agendamento]);

    // Resetar estados quando o modal fecha
    useEffect(() => {
        if (!show) {
            // Resetar toast quando modal fecha
            setShowToast(false);
            setToast({});
            // Resetar estados de loading
            setIsUploading(false);
            setIsDeleting(false);
            setIsDownloadingListaPresenca(false);
            setUploadProgress(0);
            // Fechar modal de confirmação se estiver aberto
            setShowModalConfirmacaoRemover(false);
            // Resetar indicadores de scroll
            setShowScrollTop(false);
            setShowScrollBottom(false);
        }
    }, [show]);

    // Detectar scroll e atualizar indicadores visuais
    useEffect(() => {
        const checkScroll = () => {
            if (modalBodyRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = modalBodyRef.current;
                setShowScrollTop(scrollTop > 10);
                setShowScrollBottom(scrollTop + clientHeight < scrollHeight - 10);
            }
        };

        // Copiar ref para variável local para usar no cleanup
        const modalBodyElement = modalBodyRef.current;

        if (show && modalBodyElement) {
            checkScroll();
            modalBodyElement.addEventListener('scroll', checkScroll);
            // Verificar após um pequeno delay para garantir que o conteúdo foi renderizado
            const timeoutId = setTimeout(checkScroll, 100);

            return () => {
                modalBodyElement.removeEventListener('scroll', checkScroll);
                clearTimeout(timeoutId);
            };
        }
    }, [show, agendamentoAtualizado]);

    // Função para recarregar dados do agendamento
    const recarregarAgendamento = async () => {
        if (!agendamentoAtualizado?.id) return;
        try {
            // Buscar todos os agendamentos para encontrar o atualizado
            const response = await Api.get(`/api/agendamentos-treinamentos`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Encontrar o agendamento atualizado na lista
            const agendamentoEncontrado = response.data.find(a => a.id === agendamentoAtualizado.id);
            if (agendamentoEncontrado) {
                setAgendamentoAtualizado(agendamentoEncontrado);
            } else {
                // Se não encontrou, pode ser que o agendamento tenha sido removido ou não esteja mais na lista filtrada
                // Nesse caso, tentar buscar sem filtros ou manter o estado atual
                console.warn('Agendamento não encontrado na lista atualizada');
            }
        } catch (error) {
            console.error('Erro ao recarregar agendamento:', error);
        }
    };
    
    // Função para mostrar mensagens de toast
    const showToastMessage = (header, body, variant = 'error') => {
        const type = variant === 'success' ? 'toast-success' : variant === 'danger' ? 'toast-error' : 'toast-' + variant;
        setToast({ header, body, type });
        setShowToast(true);
    };

    // Função para baixar lista de presença
    const handleDownloadListaPresenca = async () => {
        setIsDownloadingListaPresenca(true);
        try {
            const response = await Api.get('/api/download-lista-presenca', {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                responseType: 'blob'
            });
            
            // Detectar tipo de arquivo pelo content-type
            const contentType = response.headers['content-type'] || '';
            let filename = 'lista_presenca.pdf';
            
            if (contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') || 
                contentType.includes('application/vnd.ms-excel')) {
                filename = 'lista_presenca.xlsx';
            } else if (contentType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
                filename = 'lista_presenca.docx';
            } else if (contentType.includes('application/msword')) {
                filename = 'lista_presenca.doc';
            }
            
            // Criar URL do blob e fazer download
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            showToastMessage('Sucesso!', 'Lista de presença baixada com sucesso!', 'success');
        } catch (error) {
            console.error('Erro ao baixar lista de presença:', error);
            if (error.response?.status === 404) {
                showToastMessage('Aviso!', 'Lista de presença não está disponível.', 'danger');
            } else {
                showToastMessage('Erro!', 'Erro ao baixar a lista de presença. Tente novamente.', 'danger');
            }
        } finally {
            setIsDownloadingListaPresenca(false);
        }
    };

    // Função para fechar o modal limpando o toast
    const handleModalHide = () => {
        // Fechar toast se estiver aberto
        setShowToast(false);
        setToast({});
        // Chamar onHide original
        onHide();
    };
    
    if (!agendamentoAtualizado) return null;

    // Verificar se agendamento está vencido (data passou e não foi realizado/cancelado)
    // Regra: considerar vencido somente após 1 dia completo depois da data do treinamento
    const isAgendamentoVencido = (agendamento) => {
        if (!agendamento.data_treinamento) return false;
        if (agendamento.status === 'realizado' || agendamento.status === 'cancelado') return false;
        
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        
        // Tratar data no formato YYYY-MM-DD sem problemas de timezone
        let dataTreinamento;
        if (typeof agendamento.data_treinamento === 'string' && agendamento.data_treinamento.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [ano, mes, dia] = agendamento.data_treinamento.split('-');
            dataTreinamento = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
        } else {
            dataTreinamento = new Date(agendamento.data_treinamento);
        }
        dataTreinamento.setHours(0, 0, 0, 0);
        
        const diffMs = hoje.getTime() - dataTreinamento.getTime();
        if (diffMs <= 0) return false; // hoje antes ou no dia do treinamento
        const diffDias = diffMs / (1000 * 60 * 60 * 24);
        
        // Só marcar como vencido depois de 1 dia completo após a data do treinamento
        return diffDias >= 1;
    };

    const getStatusBadge = (status, agendamento = null) => {
        // Se o agendamento está vencido, mostrar "Vencido" ao invés do status original
        if (agendamento && isAgendamentoVencido(agendamento)) {
            return <Badge bg="warning">Vencido</Badge>;
        }
        
        const badges = {
            pendente: <Badge bg="warning">Pendente</Badge>,
            confirmado: <Badge bg="success">Confirmado</Badge>,
            realizado: <Badge bg="info">Realizado</Badge>,
            cancelado: <Badge bg="danger">Cancelado</Badge>
        };
        return badges[status] || <Badge bg="secondary">{status}</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [ano, mes, dia] = dateString.split('-');
            return `${dia}/${mes}/${ano}`;
        }
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR');
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        return timeString.substring(0, 5);
    };

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return '';
        const date = new Date(dateTimeString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <>
            {show && (
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    delay={4000}
                    autohide
                    onExited={() => {
                        // Limpar toast quando sair completamente
                        setToast({});
                    }}
                    className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-error'}`}
                    style={{ zIndex: 1060 }}
                >
                    <Toast.Header>
                        <strong className="me-auto">{toast.header}</strong>
                    </Toast.Header>
                    <Toast.Body>{toast.body}</Toast.Body>
                </Toast>
            )}

            <Modal 
                size="lg" 
                show={show && !showModalConfirmacaoRemover} 
                onHide={isUploading || isDeleting ? undefined : handleModalHide}
                className="modal-detalhes-agendamento"
            >
                <Modal.Header closeButton={!isUploading && !isDeleting}>
                    <span className="fw-semibold fs-5 text-primary">
                        <i className="bi bi-info-circle me-2"></i>
                        Detalhes do Agendamento
                    </span>
                </Modal.Header>
            <Modal.Body 
                ref={modalBodyRef}
                className="bg-gray-100 p-4 modal-detalhes-agendamento-body"
            >
                {showScrollTop && (
                    <div className="scroll-indicator scroll-indicator-top">
                        <i className="bi bi-chevron-up"></i>
                    </div>
                )}
                <div className="row">
                    {/* Treinamento */}
                    <div className="col-12 col-md-9 mb-3">
                        <label className="form-label text-primary fw-medium">Treinamento</label>
                        <div className="form-control border-new-light bg-white">
                            {agendamentoAtualizado.nome_curso || 'N/A'}
                        </div>
                    </div>

                    {/* Objetivo */}
                    {agendamentoAtualizado.objetivo && (
                        <div className="col-12 mb-3">
                            <label className="form-label text-primary fw-medium">Objetivo</label>
                            <div className="form-control border-new-light bg-white" style={{ minHeight: '80px' }}>
                                <div className="p-2" style={{ whiteSpace: 'pre-wrap' }}>
                                    {agendamentoAtualizado.objetivo}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Conteúdo Programático */}
                    {agendamentoAtualizado.conteudo_programatico && (
                        <div className="col-12 mb-3">
                            <label className="form-label text-primary fw-medium">Conteúdo Programático</label>
                            <div className="form-control border-new-light bg-white" style={{ minHeight: '80px' }}>
                                <div className="p-2" style={{ whiteSpace: 'pre-wrap' }}>
                                    {agendamentoAtualizado.conteudo_programatico}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Status */}
                    <div className="col-12 col-md-3 mb-3">
                        <label className="form-label text-primary fw-medium">Status</label>
                        <div className="d-flex align-items-center">
                            {getStatusBadge(agendamentoAtualizado.status, agendamentoAtualizado)}
                        </div>
                    </div>

                    {/* Alimentação */}
                    <div className="col-12 col-md-6 mb-3">
                        <label className="form-label text-primary fw-medium">Alimentação</label>
                        <div className="d-flex align-items-center">
                            {agendamentoAtualizado.alimentacao ? (
                                <Badge bg="success">Sim</Badge>
                            ) : (
                                <Badge bg="secondary">Não</Badge>
                            )}
                        </div>
                    </div>

                    {/* Carga Horária */}
                    <div className="col-12 col-md-6 mb-3">
                        <label className="form-label text-primary fw-medium">Carga Horária</label>
                        <div className="form-control border-new-light bg-white">
                            {agendamentoAtualizado.carga_horaria || 'N/A'}
                        </div>
                    </div>

                    {/* Data do Treinamento */}
                    <div className="col-12 col-md-6 mb-3">
                        <label className="form-label text-primary fw-medium">Data do Treinamento</label>
                        <div className="form-control border-new-light bg-white">
                            {formatDate(agendamentoAtualizado.data_treinamento) || 'N/A'}
                        </div>
                    </div>

                    {/* Horário */}
                    <div className="col-12 col-md-6 mb-3">
                        <label className="form-label text-primary fw-medium">Horário</label>
                        <div className="form-control border-new-light bg-white">
                            {formatTime(agendamentoAtualizado.hora_treinamento) || 'N/A'}
                        </div>
                    </div>

                    {/* Unidade */}
                    <div className="col-12 col-md-6 mb-3">
                        <label className="form-label text-primary fw-medium">Unidade</label>
                        <div className="form-control border-new-light bg-white">
                            {agendamentoAtualizado.id_unidade || 'N/A'}
                        </div>
                    </div>

                    {/* Solicitante */}
                    <div className="col-12 col-md-6 mb-3">
                        <label className="form-label text-primary fw-medium">Solicitante</label>
                        <div className="form-control border-new-light bg-white">
                            {agendamentoAtualizado.nome_solicitante || 'N/A'}
                        </div>
                    </div>

                    {/* Email do Solicitante */}
                    {agendamentoAtualizado.email_solicitante && (
                        <div className="col-12 col-md-6 mb-3">
                            <label className="form-label text-primary fw-medium">Email do Solicitante</label>
                            <div className="form-control border-new-light bg-white">
                                {agendamentoAtualizado.email_solicitante}
                            </div>
                        </div>
                    )}

                    {/* Quem Solicitou (Criador do Agendamento) */}
                    {agendamentoAtualizado.nome_quem_solicitou && (
                        <div className="col-12 col-md-6 mb-3">
                            <label className="form-label text-primary fw-medium">Criado por</label>
                            <div className="form-control border-new-light bg-white">
                                {agendamentoAtualizado.nome_quem_solicitou}
                            </div>
                        </div>
                    )}

                    {/* Email de Quem Solicitou */}
                    {agendamentoAtualizado.email_quem_solicitou && (
                        <div className="col-12 col-md-6 mb-3">
                            <label className="form-label text-primary fw-medium">Email do Criador</label>
                            <div className="form-control border-new-light bg-white">
                                {agendamentoAtualizado.email_quem_solicitou}
                            </div>
                        </div>
                    )}

                    {/* Data de Criação */}
                    {agendamentoAtualizado.data_criacao && (
                        <div className="col-12 col-md-6 mb-3">
                            <label className="form-label text-primary fw-medium">Criado em</label>
                            <div className="form-control border-new-light bg-white">
                                {formatDateTime(agendamentoAtualizado.data_criacao)}
                            </div>
                        </div>
                    )}

                    {/* Instrutor (se confirmado) */}
                    {agendamentoAtualizado.nome_instrutor && (
                        <div className="col-12 col-md-6 mb-3">
                            <label className="form-label text-primary fw-medium">Instrutor</label>
                            <div className="form-control border-new-light bg-white">
                                {agendamentoAtualizado.nome_instrutor}
                            </div>
                        </div>
                    )}

                    {/* CPF do Instrutor (se confirmado) */}
                    {agendamentoAtualizado.cpf_instrutor && (
                        <div className="col-12 col-md-6 mb-3">
                            <label className="form-label text-primary fw-medium">CPF do Instrutor</label>
                            <div className="form-control border-new-light bg-white">
                                {agendamentoAtualizado.cpf_instrutor}
                            </div>
                        </div>
                    )}

                    {/* Data de Confirmação (se confirmado) */}
                    {agendamentoAtualizado.data_confirmacao && (
                        <div className="col-12 col-md-6 mb-3">
                            <label className="form-label text-primary fw-medium">Data de Confirmação</label>
                            <div className="form-control border-new-light bg-white">
                                {formatDate(agendamentoAtualizado.data_confirmacao)}
                            </div>
                        </div>
                    )}

                    {/* Confirmado por (se confirmado) */}
                    {agendamentoAtualizado.nome_admin_confirmacao && (
                        <div className="col-12 col-md-6 mb-3">
                            <label className="form-label text-primary fw-medium">Confirmado por</label>
                            <div className="form-control border-new-light bg-white">
                                {agendamentoAtualizado.nome_admin_confirmacao}
                            </div>
                        </div>
                    )}

                    {/* Data de Realização (se realizado) */}
                    {agendamentoAtualizado.data_realizacao && (
                        <div className="col-12 col-md-6 mb-3">
                            <label className="form-label text-primary fw-medium">Data de Realização</label>
                            <div className="form-control border-new-light bg-white">
                                {formatDate(agendamentoAtualizado.data_realizacao)}
                            </div>
                        </div>
                    )}

                    {/* Relatório Crítico (se realizado) */}
                    {agendamentoAtualizado.status === 'realizado' && (
                        <div className="col-12 mb-4">
                            <label className="form-label text-primary fw-medium mb-3">Relatório Crítico</label>
                            <div className="card border-0 shadow-sm">
                                <div className="card-body p-3">
                                    {agendamentoAtualizado.relatorio_critico && agendamentoAtualizado.relatorio_critico !== null && agendamentoAtualizado.relatorio_critico !== '' ? (
                                        <div className="d-flex flex-column gap-3">
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="bg-danger bg-opacity-10 rounded-circle p-2">
                                                    <i className="bi bi-file-pdf text-danger fs-5"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="fw-medium text-dark">Relatório crítico disponível</div>
                                                    <small className="text-muted">Arquivo PDF pronto para download</small>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2 flex-wrap">
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    disabled={isUploading || isDeleting}
                                                    onClick={async () => {
                                                        try {
                                                            const response = await Api.get(`/api/download-relatorio-critico/${agendamentoAtualizado.id}`, {
                                                                headers: {
                                                                    Authorization: `Bearer ${token}`
                                                                },
                                                                responseType: 'blob'
                                                            });
                                                            
                                                            // Criar URL do blob e fazer download
                                                            const blob = new Blob([response.data], { type: 'application/pdf' });
                                                            const url = window.URL.createObjectURL(blob);
                                                            const link = document.createElement('a');
                                                            link.href = url;
                                                            link.download = `relatorio_critico_${agendamentoAtualizado.id}.pdf`;
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                            window.URL.revokeObjectURL(url);
                                                        } catch (error) {
                                                            console.error('Erro ao baixar relatório crítico:', error);
                                                            showToastMessage('Erro!', 'Erro ao baixar o relatório crítico. Tente novamente.', 'danger');
                                                        }
                                                    }}
                                                >
                                                    <i className="bi bi-download me-2"></i>
                                                    Baixar PDF
                                                </button>
                                                {isAdmin && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger"
                                                            disabled={isUploading || isDeleting}
                                                            onClick={() => {
                                                                setShowModalConfirmacaoRemover(true);
                                                            }}
                                                        >
                                                            <i className="bi bi-trash me-2"></i>
                                                            Remover
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-3">
                                            <i className="bi bi-file-pdf text-muted" style={{ fontSize: '3rem' }}></i>
                                            <p className="text-muted mb-0 mt-2">Nenhum relatório crítico disponível</p>
                                        </div>
                                    )}
                                    
                                    {isAdmin && (
                                        <div className="mt-3 pt-3 border-top">
                                            <label 
                                                htmlFor="relatorio-critico-upload" 
                                                className={`form-control label-input ${uploadProgress > 0 ? 'border-success bg-success' : 'border-primary bg-primary'} text-white fw-medium text-center cursor-pointer mb-0`}
                                                style={{ cursor: isUploading ? 'not-allowed' : 'pointer', opacity: isUploading ? 0.6 : 1 }}
                                            >
                                                {isUploading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Enviando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-cloud-upload me-2"></i>
                                                        {agendamentoAtualizado.relatorio_critico ? 'Substituir Relatório Crítico' : 'Enviar Relatório Crítico'}
                                                    </>
                                                )}
                                            </label>
                                            <input
                                                id="relatorio-critico-upload"
                                                type="file"
                                                accept=".pdf"
                                                style={{ display: 'none' }}
                                                disabled={isUploading || isDeleting}
                                                onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;
                                                
                                                // Validar se é PDF
                                                if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                                                    showToastMessage('Erro!', 'Apenas arquivos PDF são permitidos.', 'danger');
                                                    e.target.value = '';
                                                    return;
                                                }
                                                
                                                setIsUploading(true);
                                                setUploadProgress(0);
                                                
                                                try {
                                                    const formData = new FormData();
                                                    formData.append('relatorio_critico', file);
                                                    
                                                    await Api.post(`/api/upload-relatorio-critico/${agendamentoAtualizado.id}`, formData, {
                                                        headers: {
                                                            Authorization: `Bearer ${token}`,
                                                            'Content-Type': 'multipart/form-data'
                                                        },
                                                        onUploadProgress: (progressEvent) => {
                                                            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                                            setUploadProgress(percentCompleted);
                                                        }
                                                    });
                                                    
                                                    // Atualizar estado local imediatamente
                                                    setAgendamentoAtualizado(prev => ({
                                                        ...prev,
                                                        relatorio_critico: true
                                                    }));
                                                    
                                                    // Recarregar dados do agendamento
                                                    await recarregarAgendamento();
                                                    
                                                    // Atualizar lista de agendamentos no componente pai
                                                    if (onRelatorioCriticoChange) {
                                                        onRelatorioCriticoChange();
                                                    }
                                                    
                                                    setIsUploading(false);
                                                    setUploadProgress(0);
                                                    e.target.value = '';
                                                    
                                                    // Mostrar toast de sucesso
                                                    showToastMessage('Sucesso!', 'Relatório crítico enviado com sucesso!', 'success');
                                                } catch (error) {
                                                    console.error('Erro ao enviar relatório crítico:', error);
                                                    showToastMessage('Erro!', error.response?.data?.message || 'Erro ao enviar o relatório crítico. Tente novamente.', 'danger');
                                                    setIsUploading(false);
                                                    setUploadProgress(0);
                                                    e.target.value = '';
                                                }
                                            }}
                                        />
                                        {uploadProgress > 0 && uploadProgress < 100 && (
                                            <div className="progress mt-2">
                                                <div 
                                                    className="progress-bar" 
                                                    role="progressbar" 
                                                    style={{ width: `${uploadProgress}%` }}
                                                    aria-valuenow={uploadProgress} 
                                                    aria-valuemin="0" 
                                                    aria-valuemax="100"
                                                >
                                                    {uploadProgress}%
                                                </div>
                                            </div>
                                        )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Lista de Presença */}
                    {podeBaixarListaPresenca && (
                        <div className="col-12 mb-4">
                            <label className="form-label text-primary fw-medium mb-3">Lista de Presença</label>
                            <div className="card border-0 shadow-sm">
                                <div className="card-body p-3">
                                    <div className="d-flex flex-column gap-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="bg-success rounded-circle px-3 py-2">
                                                <i className="bi bi-file-earmark-text text-white fs-5"></i>
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="fw-medium text-dark">Lista de presença disponível</div>
                                                <small className="text-muted">Arquivo pronto para download</small>
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                disabled={isDownloadingListaPresenca || isUploading || isDeleting}
                                                onClick={handleDownloadListaPresenca}
                                            >
                                                {isDownloadingListaPresenca ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Baixando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-download me-2"></i>
                                                        Baixar Lista de Presença
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Justificativa de Cancelamento (se cancelado) */}
                    {agendamentoAtualizado.justificativa_cancelamento && (
                        <div className="col-12 mb-3">
                            <label className="form-label text-primary fw-medium">Justificativa de Cancelamento</label>
                            <div className="form-control border-new-light bg-white">
                                {agendamentoAtualizado.justificativa_cancelamento}
                            </div>
                        </div>
                    )}

                    {/* Taxa Cobrada (se cancelado) */}
                    {agendamentoAtualizado.status === 'cancelado' && (
                        <div className="col-12 col-md-6 mb-3">
                            <label className="form-label text-primary fw-medium">Taxa Cobrada</label>
                            <div className="d-flex align-items-center">
                                {agendamentoAtualizado.cobrar_taxa ? (
                                    <Badge bg="warning">Sim</Badge>
                                ) : (
                                    <Badge bg="secondary">Não</Badge>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Observações */}
                    <div className="col-12 mb-3">
                        <label className="form-label text-primary fw-medium">Observações</label>
                        <div className="form-control border-new-light bg-white" style={{ minHeight: '100px' }}>
                            {agendamentoAtualizado.observacoes ? (
                                <div className="p-2">{agendamentoAtualizado.observacoes}</div>
                            ) : (
                                <div className="p-2 text-muted">Nenhuma observação registrada</div>
                            )}
                        </div>
                    </div>
                </div>
                {showScrollBottom && (
                    <div className="scroll-indicator scroll-indicator-bottom">
                        <i className="bi bi-chevron-down"></i>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="justify-content-end">
                <div className="d-flex justify-content-end">
                    <button 
                        type="button" 
                        className="btn btn-primary" 
                        onClick={handleModalHide}
                        disabled={isUploading || isDeleting}
                    >
                        Fechar
                    </button>
                </div>
            </Modal.Footer>
        </Modal>

        {/* Modal de Confirmação para Remover Relatório Crítico */}
        <Modal show={showModalConfirmacaoRemover} onHide={isDeleting ? undefined : () => {
            setShowModalConfirmacaoRemover(false);
            // Reabrir modal de detalhes ao cancelar
            if (show) {
                // O modal de detalhes será reaberto automaticamente quando showModalConfirmacaoRemover for false
            }
        }}>
            <Modal.Header closeButton={!isDeleting}>
                <span className="fw-semibold fs-5 text-primary">
                    <i className="bi bi-exclamation-triangle-fill me-2 text-warning"></i>
                    Confirmar Remoção
                </span>
            </Modal.Header>
            <Modal.Body className="bg-gray-100 p-4">
                <p className="mb-0">Tem certeza que deseja remover o relatório crítico?</p>
                <p className="text-muted mt-2 mb-0">Esta ação não pode ser desfeita.</p>
            </Modal.Body>
            <Modal.Footer className="justify-content-end">
                <div className="d-flex justify-content-end">
                    <button
                        type="button"
                        className="btn me-2"
                        disabled={isDeleting}
                        onClick={() => {
                            setShowModalConfirmacaoRemover(false);
                        }}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-danger"
                        disabled={isDeleting}
                        onClick={async () => {
                            setIsDeleting(true);
                            try {
                                await Api.delete(`/api/remover-relatorio-critico/${agendamentoAtualizado.id}`, {
                                    headers: {
                                        Authorization: `Bearer ${token}`
                                    }
                                });
                                
                                // Atualizar estado local imediatamente
                                setAgendamentoAtualizado(prev => ({
                                    ...prev,
                                    relatorio_critico: null
                                }));
                                
                                setShowModalConfirmacaoRemover(false);
                                
                                // Recarregar dados do agendamento
                                await recarregarAgendamento();
                                
                                // Atualizar lista de agendamentos no componente pai
                                if (onRelatorioCriticoChange) {
                                    onRelatorioCriticoChange();
                                }
                                
                                setIsDeleting(false);
                                
                                // Mostrar toast de sucesso
                                showToastMessage('Sucesso!', 'Relatório crítico removido com sucesso!', 'success');
                            } catch (error) {
                                console.error('Erro ao remover relatório crítico:', error);
                                setShowModalConfirmacaoRemover(false);
                                setIsDeleting(false);
                                showToastMessage('Erro!', error.response?.data?.message || 'Erro ao remover o relatório crítico. Tente novamente.', 'danger');
                            }
                        }}
                    >
                        {isDeleting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Removendo...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-trash me-2"></i>
                                Remover
                            </>
                        )}
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
        </>
    );
}

export default ModalDetalhesAgendamento;

